import { useEffect, useState, lazy, Suspense } from 'react'
import useThemeStore from './store/themeStore'
import useI18nStore from './store/i18nStore'
import { useRegisterSW } from 'virtual:pwa-register/react'
import { usePushNotifications } from './hooks/usePushNotifications'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { getSessionSafe } from './lib/supabase'
import { scheduleTrafficRefresh } from './lib/checkTrafficReminder'
import useAuthStore from './store/authStore'
import useTaskStore from './store/taskStore'
import usePracticeStore from './store/practiceStore'
import useLocationReminderStore from './store/locationReminderStore'
import { useT } from './i18n/useT'
import { CheckCircle2 } from 'lucide-react'
import Layout from './components/Layout'
import QuickAddModal from './components/QuickAddModal'
import PremiumModal from './components/PremiumModal'
import ChatAgentModal from './components/ChatAgentModal'
import SyncToast from './components/SyncToast'
import InstallPrompt from './components/InstallPrompt'
import ResetPasswordModal from './components/ResetPasswordModal'

const LandingPage   = lazy(() => import('./pages/LandingPage'))
const LoginPage     = lazy(() => import('./pages/LoginPage'))
const RegisterPage  = lazy(() => import('./pages/RegisterPage'))
const DashboardPage = lazy(() => import('./pages/DashboardPage'))
const TasksPage     = lazy(() => import('./pages/TasksPage'))
const DeadlinesPage = lazy(() => import('./pages/DeadlinesPage'))
const BlocchiPage   = lazy(() => import('./pages/BlocchiPage'))
const ProfilePage   = lazy(() => import('./pages/ProfilePage'))
const PrivacyPage   = lazy(() => import('./pages/PrivacyPage'))
const TermsPage     = lazy(() => import('./pages/TermsPage'))
const CalendarPage  = lazy(() => import('./pages/CalendarPage'))

function UpdateBanner() {
  const t = useT()
  const user = useAuthStore(s => s.user)
  const { needRefresh: [needRefresh], updateServiceWorker } = useRegisterSW({
    onRegisteredSW(_swUrl, r) {
      r.update()
      setInterval(() => r.update(), 60 * 60 * 1000) // check ogni ora
    }
  })
  const [updating, setUpdating] = useState(false)
  // Show only when the user is authenticated — no banner on public landing/auth pages.
  if (!user) return null
  if (!needRefresh) return null

  const handleUpdate = async () => {
    setUpdating(true)
    await updateServiceWorker(true)
  }

  // Dialog bloccante: overlay scuro + modal centrata. Nessuna via di fuga
  // (no tasto chiudi, no click outside) — l'unica azione è "Aggiorna".
  return (
    <div className="update-modal-overlay" role="dialog" aria-modal="true">
      <div className="update-modal">
        <img
          src="/icon-512.png"
          alt="Mindesk"
          className="update-modal-icon update-modal-icon--img"
        />
        <h2 className="update-modal-title">{t('update_title')}</h2>
        <p className="update-modal-text">{t('update_message')}</p>
        <button
          className="btn btn-primary update-modal-btn"
          onClick={handleUpdate}
          disabled={updating}
        >
          {updating ? t('update_updating') : t('update_button')}
        </button>
      </div>
    </div>
  )
}

function ProtectedRoute({ children }) {
  const user    = useAuthStore(s => s.user)
  const loading = useAuthStore(s => s.loading)
  if (loading) return <div className="app-loading"><span className="app-loading-spinner" /></div>
  if (!user) return <Navigate to="/login" replace />
  return children
}

export default function App() {
  const user             = useAuthStore(s => s.user)
  const loading          = useAuthStore(s => s.loading)
  const passwordRecovery = useAuthStore(s => s.passwordRecovery)
  const init    = useAuthStore(s => s.init)

  const t = useT()

  const isQuickAddOpen      = useTaskStore(s => s.isQuickAddOpen)
  const quickAddMode        = useTaskStore(s => s.quickAddMode)
  const quickAddInitialData = useTaskStore(s => s.quickAddInitialData)
  const closeQuickAdd       = useTaskStore(s => s.closeQuickAdd)
  const addTask             = useTaskStore(s => s.addTask)
  const updateTask          = useTaskStore(s => s.updateTask)
  const toggleTask          = useTaskStore(s => s.toggleTask)
  const locationReminderCreateForTask = useLocationReminderStore(s => s.createForTask)
  const locationReminderUpdateForTask = useLocationReminderStore(s => s.updateForTask)
  const locationReminderDeleteForTask = useLocationReminderStore(s => s.deleteForTask)
  const isPremiumModalOpen     = useTaskStore(s => s.isPremiumModalOpen)
  const premiumModalHighlight = useTaskStore(s => s.premiumModalHighlight)
  const closePremiumModal     = useTaskStore(s => s.closePremiumModal)
  const isAgentOpen          = useTaskStore(s => s.isAgentOpen)
  const agentInitialMessage  = useTaskStore(s => s.agentInitialMessage)
  const closeAgent           = useTaskStore(s => s.closeAgent)
  const refreshProfile      = useAuthStore(s => s.refreshProfile)
  const initRealtime        = useTaskStore(s => s.initRealtime)
  const destroyRealtime     = useTaskStore(s => s.destroyRealtime)
  const initPracticesRT     = usePracticeStore(s => s.initRealtime)
  const destroyPracticesRT  = usePracticeStore(s => s.destroyRealtime)
  const initLocationReminderRT     = useLocationReminderStore(s => s.initRealtime)
  const destroyLocationReminderRT  = useLocationReminderStore(s => s.destroyRealtime)

  const theme = useThemeStore(s => s.theme)
  const lang  = useI18nStore(s => s.lang)
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme)
    document.documentElement.lang = lang === 'it' ? 'it-IT' : 'en-GB'
  }, [theme, lang])

  usePushNotifications()

  // Avvia/ferma Realtime al cambio utente — una sola subscription per tutta l'app
  useEffect(() => {
    if (user?.id) {
      initRealtime()
      initPracticesRT()
      initLocationReminderRT()
      return () => {
        destroyRealtime()
        destroyPracticesRT()
        destroyLocationReminderRT()
      }
    }
  }, [user?.id])

  // Refresh session + check shares when app regains focus
  const checkPendingShares = useAuthStore(s => s.checkPendingShares)
  useEffect(() => {
    if (!user) return
    const handler = async () => {
      if (document.hidden) return
      // 1) Refresh session (getSessionSafe ha timeout + fallback cache)
      await getSessionSafe()

      // 2) Probe call: dopo un lungo background mobile, anche dopo refresh, il
      //    token cached può essere expired e il refresh può aver fallito.
      //    Facciamo una chiamata leggera per verificare: se 401 → ricarichiamo
      //    la pagina, così Supabase init riparte fresh con token valido.
      try {
        const session = useAuthStore.getState().session
        if (session?.access_token) {
          const probeUrl = `${import.meta.env.VITE_SUPABASE_URL}/rest/v1/profiles?id=eq.${user.id}&select=id`
          const res = await fetch(probeUrl, {
            headers: {
              'apikey':        import.meta.env.VITE_SUPABASE_ANON_KEY,
              'Authorization': `Bearer ${session.access_token}`,
            },
          })
          if (res.status === 401) {
            console.warn('[focus probe] JWT expired, reloading page')
            window.location.reload()
            return
          }
        }
      } catch (e) {
        // Network issue / offline: NON ricarichiamo (potrebbe essere temporaneo).
        // Quando la connessione torna, il prossimo visibilitychange ritenterà.
        console.warn('[focus probe] failed:', e?.message)
      }

      checkPendingShares()
    }
    document.addEventListener('visibilitychange', handler)
    return () => document.removeEventListener('visibilitychange', handler)
  }, [user?.id]) // eslint-disable-line

  const [saveToast, setSaveToast] = useState('')

  const handleSave = async (data) => {
    // `_targetId` viene passato da ManualForm in post-create per propagare
    // eventuali edit al task appena creato senza ri-crearlo. quickAddInitialData?.id
    // copre il caso "edit di task esistente" aperto dal parent.
    const { _targetId, trafficLocation, ...payload } = data
    console.log('[handleSave] trafficLocation:', trafficLocation)
    const targetId = quickAddInitialData?.id || _targetId
    if (targetId) {
      await updateTask(targetId, payload)
      // Handle location reminder update/delete
      if (trafficLocation) {
        console.log('[handleSave] Creating location reminder for update:', targetId)
        await locationReminderUpdateForTask(targetId, {
          destinationAddress: trafficLocation.destAddr,
          destinationLat: trafficLocation.destLat,
          destinationLon: trafficLocation.destLon,
          originAddress: trafficLocation.originAddr,
          originLat: trafficLocation.originLat,
          originLon: trafficLocation.originLon,
          arriveBy: new Date(`${payload.dueDate}T${payload.dueTime}`).toISOString(),
        })
        // Trigger immediate traffic check
        triggerTrafficCheck()
      } else {
        // Delete location reminder if traffic data was cleared
        await locationReminderDeleteForTask(targetId).catch(() => {})
      }
      return targetId
    }
    const newId = await addTask(payload)
    // Handle location reminder creation
    if (trafficLocation && newId) {
      console.log('[handleSave] Creating location reminder for new task:', newId, trafficLocation)
      await locationReminderCreateForTask(newId, {
        destinationAddress: trafficLocation.destAddr,
        destinationLat: trafficLocation.destLat,
        destinationLon: trafficLocation.destLon,
        originAddress: trafficLocation.originAddr,
        originLat: trafficLocation.originLat,
        originLon: trafficLocation.originLon,
        arriveBy: new Date(`${payload.dueDate}T${payload.dueTime}`).toISOString(),
      }).catch(err => console.error('Location reminder creation failed:', err))
      // Schedule location reminder refresh after cron runs
      scheduleTrafficRefresh(newId)
    } else {
      console.log('[handleSave] No traffic location or task ID, skipping reminder creation', { trafficLocation, newId })
    }
    // Show toast if task won't be visible in urgent list (due date > 7 days or no date)
    if (payload.dueDate) {
      const days = Math.ceil((new Date(payload.dueDate) - new Date()) / 86400000)
      if (days > 7) {
        setSaveToast('task_created_calendar')
        setTimeout(() => setSaveToast(''), 3000)
      }
    }
    return newId
  }

  useEffect(() => { init() }, [init])

  // After Stripe redirects back with ?payment=success, poll until plan = 'pro'
  // (webhook may take a moment to process after the redirect)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    if (params.get('payment') !== 'success') return
    window.history.replaceState({}, '', window.location.pathname)

    let attempts = 0
    const tryRefresh = async () => {
      await refreshProfile()
      attempts++
      const plan = useAuthStore.getState().user?.plan
      if (plan === 'free' && attempts < 4) setTimeout(tryRefresh, 2000)
    }
    setTimeout(tryRefresh, 1500)
  }, [])

  return (
    <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <UpdateBanner />
      <SyncToast />
      <Suspense fallback={<div className="app-loading"><span className="app-loading-spinner" /></div>}>
      <Routes>
        <Route path="/login"    element={!loading && user ? <Navigate to="/" replace /> : <LoginPage />} />
        <Route path="/register" element={!loading && user ? <Navigate to="/" replace /> : <RegisterPage />} />
        <Route path="/" element={
          loading
            ? <div className="app-loading"><span className="app-loading-spinner" /></div>
            : user
              ? <Layout><DashboardPage /></Layout>
              : <LandingPage />
        } />
        <Route path="/attivita" element={
          <ProtectedRoute><Layout><TasksPage /></Layout></ProtectedRoute>
        } />
        <Route path="/scadenze" element={
          <ProtectedRoute><Layout><DeadlinesPage /></Layout></ProtectedRoute>
        } />
        <Route path="/calendario" element={
          <ProtectedRoute><Layout><CalendarPage /></Layout></ProtectedRoute>
        } />
        <Route path="/blocchi" element={
          <ProtectedRoute><Layout><BlocchiPage /></Layout></ProtectedRoute>
        } />
        <Route path="/profilo" element={
          <ProtectedRoute><Layout><ProfilePage /></Layout></ProtectedRoute>
        } />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/terms"   element={<TermsPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </Suspense>

      {isQuickAddOpen && (
        <QuickAddModal
          mode={quickAddMode}
          initialData={quickAddInitialData}
          onClose={closeQuickAdd}
          onSave={handleSave}
          onToggle={quickAddInitialData?.id ? () => { toggleTask(quickAddInitialData.id); closeQuickAdd() } : null}
        />
      )}

      {isPremiumModalOpen && (
        <PremiumModal onClose={closePremiumModal} highlightPlan={premiumModalHighlight} />
      )}

      {isAgentOpen && (
        <ChatAgentModal
          onClose={closeAgent}
          autoListen={!agentInitialMessage}
          initialMessage={agentInitialMessage}
        />
      )}

      {user && <InstallPrompt />}
      {passwordRecovery && <ResetPasswordModal />}

      {saveToast && (
        <div className="save-toast">
          <CheckCircle2 size={16} />
          {t(saveToast)}
        </div>
      )}
    </BrowserRouter>
  )
}
